import { useEffect, useState, useCallback, useMemo } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  CalendarDays, MapPin, Loader2, Eye, EyeOff, Sparkles, Store, PartyPopper,
} from "lucide-react";

/**
 * MyCalendarPage — the profile owner's personal calendar at `/calendar`.
 *
 * Displays every event / approved application / paid booking, grouped by
 * month, with a per-item visibility switch. Toggle state is optimistic:
 * flip locally first, PATCH the server, roll back on error.
 *
 * The public counterpart (`PublicCalendarSection`) renders a subset of
 * the same shape — anything the owner has NOT flipped public here won't
 * appear on the profile that QR scanners land on.
 */
const KIND_META = {
  event:       { icon: Sparkles,   label: "Hosting",        color: "var(--avt-purple)" },
  application: { icon: Store,      label: "Selling at",     color: "var(--avt-teal)" },
  booking:     { icon: PartyPopper, label: "Private booking", color: "var(--avt-pink)" },
};

export default function MyCalendarPage() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includePast, setIncludePast] = useState(false);

  const load = useCallback(async (past = includePast) => {
    setLoading(true);
    try {
      const { data } = await api.get("/users/me/calendar",
        { params: { include_past: past } });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Couldn't load your calendar");
    } finally { setLoading(false); }
  }, [includePast]);

  useEffect(() => {
    if (user) load(includePast);
  }, [user, includePast, load]);

  const grouped = useMemo(() => {
    // Group by "YYYY-MM" so months become natural sections.
    const map = new Map();
    items.forEach((it) => {
      const ym = it.date.slice(0, 7);
      if (!map.has(ym)) map.set(ym, []);
      map.get(ym).push(it);
    });
    return Array.from(map.entries());
  }, [items]);

  const toggle = async (item, next) => {
    // Optimistic update
    setItems((cur) => cur.map((x) =>
      (x.kind === item.kind && x.source_id === item.source_id) ? { ...x, is_public: next } : x
    ));
    try {
      await api.patch(`/users/me/calendar/${item.kind}/${item.source_id}`,
        { public: next });
    } catch (e) {
      // Rollback on failure
      setItems((cur) => cur.map((x) =>
        (x.kind === item.kind && x.source_id === item.source_id) ? { ...x, is_public: !next } : x
      ));
      toast.error("Couldn't update visibility");
    }
  };

  if (authLoading) return null;
  if (!user) return <Navigate to="/login?next=/calendar" replace />;

  const publicSlug = user.slug;

  return (
    <div className="mx-auto max-w-4xl px-6 py-10" data-testid="my-calendar-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="overline" style={{ color: "var(--avt-purple)" }}>Your calendar</div>
          <h1 className="font-serif text-4xl mt-2">Every date, in one place.</h1>
          <p className="mt-1 text-sm max-w-2xl" style={{ color: "var(--ink-soft)" }}>
            Events you're hosting, gigs you've been approved for, and private
            bookings — auto-synced. Flip the switch to control what shows up
            on your public profile when someone scans your QR code.
          </p>
        </div>
        {publicSlug && (
          <Link
            to={`/vendors/${publicSlug}`}
            target="_blank"
            className="text-sm underline inline-flex items-center gap-1.5"
            style={{ color: "var(--avt-purple)" }}
            data-testid="view-public-profile-link"
          >
            View my public profile →
          </Link>
        )}
      </div>

      <div className="mt-6 flex items-center gap-2">
        <button
          onClick={() => setIncludePast(false)}
          data-testid="calendar-filter-upcoming"
          className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
          style={{
            backgroundColor: !includePast ? "var(--avt-purple)" : "transparent",
            color: !includePast ? "#fff" : "var(--ink)",
            borderColor: !includePast ? "var(--avt-purple)" : "var(--border-warm)",
          }}
        >
          Upcoming
        </button>
        <button
          onClick={() => setIncludePast(true)}
          data-testid="calendar-filter-all"
          className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
          style={{
            backgroundColor: includePast ? "var(--avt-purple)" : "transparent",
            color: includePast ? "#fff" : "var(--ink)",
            borderColor: includePast ? "var(--avt-purple)" : "var(--border-warm)",
          }}
        >
          All time
        </button>
      </div>

      <div className="mt-6 space-y-8">
        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="h-8 w-8 mx-auto animate-spin" style={{ color: "var(--avt-purple)" }} />
          </div>
        ) : items.length === 0 ? (
          <div className="card-warm rounded-2xl p-10 text-center"
               data-testid="calendar-empty">
            <CalendarDays className="h-10 w-10 mx-auto" style={{ color: "var(--avt-purple)" }} />
            <h2 className="mt-4 font-serif text-2xl">Nothing scheduled yet.</h2>
            <p className="mt-2 text-sm max-w-md mx-auto" style={{ color: "var(--ink-soft)" }}>
              As you approve applications and confirm private bookings, dates
              will start filling in here — nothing to manage manually.
            </p>
          </div>
        ) : (
          grouped.map(([ym, entries]) => (
            <MonthBlock key={ym} yearMonth={ym} entries={entries} onToggle={toggle} />
          ))
        )}
      </div>
    </div>
  );
}

function MonthBlock({ yearMonth, entries, onToggle }) {
  const [year, month] = yearMonth.split("-");
  const monthName = new Date(Number(year), Number(month) - 1, 1)
    .toLocaleDateString(undefined, { month: "long", year: "numeric" });
  return (
    <section data-testid={`calendar-month-${yearMonth}`}>
      <div className="overline mb-3" style={{ color: "var(--ink-soft)" }}>
        {monthName}
      </div>
      <ul className="space-y-2">
        {entries.map((e) => (
          <li key={`${e.kind}:${e.source_id}`}>
            <CalendarRow entry={e} onToggle={onToggle} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function CalendarRow({ entry, onToggle }) {
  const meta = KIND_META[entry.kind] || KIND_META.event;
  const Icon = meta.icon;
  const dayNum = Number(entry.date.slice(8, 10));
  const dayLabel = new Date(entry.date).toLocaleDateString(undefined, { weekday: "short" });
  const testKey = `calendar-row-${entry.kind}-${entry.source_id}`;
  return (
    <div
      className="card-warm rounded-2xl p-4 flex items-start gap-4"
      data-testid={testKey}
    >
      <div className="text-center px-3" style={{ minWidth: 64 }}>
        <div className="text-[10px] uppercase tracking-widest"
             style={{ color: "var(--ink-soft)" }}>{dayLabel}</div>
        <div className="font-serif text-3xl leading-none" style={{ color: "var(--avt-purple)" }}>
          {dayNum}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest font-semibold inline-flex items-center gap-1"
                style={{ color: meta.color }}>
            <Icon className="h-3 w-3" /> {meta.label}
          </span>
        </div>
        <div className="font-semibold mt-0.5 truncate">
          {entry.url ? (
            <Link to={entry.url} className="hover:underline">{entry.title}</Link>
          ) : entry.title}
          {entry.kind === "booking" && entry.customer_name && (
            <span className="text-xs font-normal ml-2" style={{ color: "var(--ink-soft)" }}>
              · {entry.customer_name}
            </span>
          )}
        </div>
        {(entry.city || entry.location) && (
          <div className="text-xs mt-1 flex items-center gap-1" style={{ color: "var(--ink-soft)" }}>
            <MapPin className="h-3 w-3" /> {entry.location || entry.city}
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1.5">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <span className="text-[11px]" style={{ color: entry.is_public ? "var(--avt-purple)" : "var(--ink-soft)" }}>
            {entry.is_public
              ? <><Eye className="h-3 w-3 inline mr-1" /> Public</>
              : <><EyeOff className="h-3 w-3 inline mr-1" /> Private</>}
          </span>
          <Switch
            checked={!!entry.is_public}
            onCheckedChange={(v) => onToggle(entry, v)}
            data-testid={`${testKey}-toggle`}
          />
        </label>
        {entry.kind === "booking" && entry.is_public && (
          <span className="text-[10px]" style={{ color: "var(--ink-soft)", maxWidth: 140, textAlign: "right" }}>
            Only "Booked" shows publicly — details stay private.
          </span>
        )}
      </div>
    </div>
  );
}
